if (!customElements.get('lc-sticky-product-bar')) {
  class LCStickyProductBar extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.mainProduct = document.getElementById(this.dataset.productRoot);
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-lc-sticky-variant-id]');
      this.variantSelect = this.querySelector('[data-lc-sticky-variant-select]');
      this.quantityInput = this.querySelector('input[name="quantity"]');
      this.submitButton = this.querySelector('button[name="add"]');
      this.submitLabel = this.querySelector('[data-lc-sticky-submit-label]');
      this.submitLabelMobile = this.querySelector('[data-lc-sticky-submit-label-mobile]');
      this.price = this.querySelector('[data-lc-sticky-price]');
      this.image = this.querySelector('.lc-sticky-product-bar__image img');
      this.status = this.querySelector('[data-lc-sticky-status]');
      this.dismissButton = this.querySelector('[data-lc-sticky-dismiss]');
      this.primaryActions = null;
      this.scrollFrame = null;
      this.dismissed = false;

      if (!this.mainProduct || !this.form || !this.submitButton) return;

      this.onScroll = this.requestVisibilityUpdate.bind(this);
      this.onResize = this.updateHeight.bind(this);
      this.onVariantSelect = this.updateSelectedVariant.bind(this);
      this.onSubmit = this.submit.bind(this);
      this.onMainProductUpdate = this.handleMainProductUpdate.bind(this);
      this.onDismiss = this.dismiss.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.form.addEventListener('submit', this.onSubmit);
      this.variantSelect?.addEventListener('change', this.onVariantSelect);
      this.dismissButton?.addEventListener('click', this.onDismiss);

      this.mainProductObserver = new MutationObserver(this.onMainProductUpdate);
      this.mainProductObserver.observe(this.mainProduct, {
        childList: true,
        subtree: true
      });

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);

      document.body.classList.toggle(
        'lc-shopify-preview-bar-active',
        Boolean(document.getElementById('PBarNextFrame'))
      );

      this.refreshPrimaryActions();
      this.syncFromMainProduct();
      this.updateHeight();
      this.updateVisibility();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.variantSelect?.removeEventListener('change', this.onVariantSelect);
      this.dismissButton?.removeEventListener('click', this.onDismiss);
      this.mainProductObserver?.disconnect();
      this.resizeObserver?.disconnect();

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      document.body.classList.remove('lc-sticky-product-bar-visible');
      document.body.classList.remove('lc-shopify-preview-bar-active');
    }

    get cartDrawer() {
      return document.querySelector('cart-drawer');
    }

    handleMainProductUpdate() {
      this.refreshPrimaryActions();
      this.syncFromMainProduct();
      this.requestVisibilityUpdate();
    }

    refreshPrimaryActions() {
      const nextPrimaryActions = this.mainProduct.querySelector('.product__actions');

      if (nextPrimaryActions === this.primaryActions) return;

      this.primaryActionsObserver?.disconnect();
      this.primaryActions = nextPrimaryActions;

      if (!this.primaryActions) return;

      this.primaryActionsObserver = new IntersectionObserver(
        () => this.requestVisibilityUpdate(),
        { threshold: [0, 1] }
      );
      this.primaryActionsObserver.observe(this.primaryActions);
    }

    requestVisibilityUpdate() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      if (!this.primaryActions?.isConnected) {
        this.refreshPrimaryActions();
      }

      const actionsRect = this.primaryActions?.getBoundingClientRect();
      const shouldShow = !this.dismissed && Boolean(actionsRect && actionsRect.bottom < 0);

      this.classList.toggle('is-visible', shouldShow);
      this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      document.body.classList.toggle('lc-sticky-product-bar-visible', shouldShow);

      if (shouldShow) this.updateHeight();
    }

    dismiss() {
      this.dismissed = true;
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lc-sticky-product-bar-visible');
    }

    updateHeight() {
      document.documentElement.style.setProperty(
        '--lc-sticky-product-bar-height',
        `${Math.ceil(this.getBoundingClientRect().height)}px`
      );
    }

    syncFromMainProduct() {
      const selectedVariantElement = this.mainProduct.querySelector('[data-selected-variant]');

      if (!selectedVariantElement?.textContent.trim()) return;

      try {
        const selectedVariant = JSON.parse(selectedVariantElement.textContent);
        const matchingOption = this.variantSelect?.querySelector(
          `option[value="${selectedVariant.id}"]`
        );

        if (matchingOption) {
          this.variantSelect.value = String(selectedVariant.id);
          this.applyOption(matchingOption);
        } else {
          this.variantInput.value = selectedVariant.id;
          this.setAvailability(selectedVariant.available);
        }
      } catch (error) {
        console.error(error);
      }
    }

    updateSelectedVariant() {
      const selectedOption = this.variantSelect?.selectedOptions[0];
      if (!selectedOption) return;

      this.applyOption(selectedOption);
    }

    applyOption(option) {
      this.variantInput.value = option.value;

      if (this.price && option.dataset.price) {
        this.price.textContent = option.dataset.price;
      }

      if (this.image && option.dataset.image) {
        this.image.src = option.dataset.image;
        this.image.srcset = '';
      }

      if (this.quantityInput) {
        if (option.dataset.max) {
          this.quantityInput.max = option.dataset.max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
      }

      this.setAvailability(option.dataset.available === 'true');
    }

    setAvailability(available) {
      this.submitButton.disabled = !available;
      this.submitLabel.textContent = available
        ? this.dataset.addLabel
        : this.dataset.soldOutLabel;
      this.submitLabelMobile.textContent = available
        ? this.dataset.addMobileLabel
        : this.dataset.soldOutMobileLabel;
    }

    async submit(event) {
      event.preventDefault();

      if (this.submitButton.disabled) return;

      this.submitButton.classList.add('is-loading');
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.status.textContent = '';

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);

      if (this.cartDrawer) {
        formData.append(
          'sections',
          this.cartDrawer
            .getSectionsToRender()
            .map(section => section.section)
        );
      }

      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      try {
        const response = await fetch(routes.cart_add_url, config);
        const result = await response.json();

        if (result.status) {
          this.status.textContent = result.description;
          return;
        }

        this.status.textContent = this.dataset.successLabel;

        if (this.dataset.cartType === 'page' || Shopify.template === 'cart') {
          window.location.reload();
        } else if (this.dataset.cartType === 'drawer') {
          this.cartDrawer?.renderContents(result);
        }
      } catch (error) {
        console.error(error);
        this.status.textContent = window.cartStrings?.error || '';
      } finally {
        this.submitButton.classList.remove('is-loading');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('lc-sticky-product-bar', LCStickyProductBar);
}
